import React, { useState, useEffect } from "react";
import { Modal, Select, message } from "antd";
import {
  ShoppingCartOutlined,
  DisconnectOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import {
  useGetConnectedEbayStoreQuery,
  useInitiateEbayOAuthMutation,
  useDisconnectEbayMutation,
} from "@/api/ebayApi";

const PlatformCard = styled.div`
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  padding: 24px;
  width: 280px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 14px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CardTitle = styled.h3`
  font-family: Poppins;
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
  flex: 1;
`;

const ConnectedBadge = styled.span`
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 10px;
  padding: 2px 10px;
  font-size: 12px;
  font-family: Inter;
  color: #d46b08;
  font-weight: 500;
`;

const CardDesc = styled.p`
  font-family: Inter;
  font-size: 13px;
  color: #585858;
  margin: 0;
  line-height: 1.6;
`;

const StoreLabel = styled.div`
  font-family: Inter;
  font-size: 14px;
  color: #585858;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 8px 12px;
  word-break: break-all;
`;

const EnvTag = styled.span`
  font-family: Inter;
  font-size: 11px;
  color: #9ca3af;
  background: #f0f0f0;
  border-radius: 4px;
  padding: 2px 6px;
  margin-left: 6px;
  text-transform: capitalize;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ConnectBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background-color: #000;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  &:hover {
    background-color: #333;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ExternalBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  padding: 6px 12px;
  font-family: Inter;
  font-size: 13px;
  color: #585858;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #aaa;
    color: #000;
  }
`;

const DisconnectBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid #ffd8d8;
  border-radius: 6px;
  padding: 6px 12px;
  font-family: Inter;
  font-size: 13px;
  color: #ff4d4f;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #fff1f0;
    border-color: #ff4d4f;
  }
`;

export const EbayConnect = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [marketplaceId, setMarketplaceId] = useState("EBAY_US");
  const { data: store, refetch } = useGetConnectedEbayStoreQuery(
    undefined,
    { skip: false }
  );
  const [initiateOAuth, { isLoading: isInitiating }] =
    useInitiateEbayOAuthMutation();
  const [disconnectStore, { isLoading: isDisconnecting }] =
    useDisconnectEbayMutation();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleConnect = async () => {
    setIsModalVisible(false);
    try {
      const result = await initiateOAuth({ marketplaceId }).unwrap();
      if (result.authUrl) {
        window.location.href = result.authUrl;
      }
    } catch (error) {
      message.error(
        error?.data?.message || "Failed to initiate eBay connection"
      );
    }
  };

  const handleDisconnect = async () => {
    Modal.confirm({
      title: "Disconnect eBay?",
      content: "Are you sure you want to disconnect your eBay account?",
      okText: "Yes",
      cancelText: "No",
      onOk: async () => {
        try {
          await disconnectStore().unwrap();
          message.success("eBay disconnected successfully");
          refetch();
        } catch (error) {
          message.error(
            error?.data?.message || "Failed to disconnect eBay"
          );
        }
      },
    });
  };

  if (store && store.id) {
    return (
      <PlatformCard>
        <CardHeader>
          <ShoppingCartOutlined style={{ fontSize: 22, color: "#ed6928" }} />
          <CardTitle>eBay</CardTitle>
          <ConnectedBadge>Connected</ConnectedBadge>
        </CardHeader>
        <StoreLabel>
          {store.storeName}
          {store.environment && <EnvTag>{store.marketplaceId}</EnvTag>}
        </StoreLabel>
        <ActionRow>
          <ExternalBtn
            onClick={() =>
              window.open(
                store.environment === "production"
                  ? "https://www.ebay.com/myb/Selling"
                  : "https://www.sandbox.ebay.com/myb/Selling",
                "_blank"
              )
            }
          >
            <ExportOutlined /> Open eBay
          </ExternalBtn>
          <DisconnectBtn onClick={handleDisconnect} disabled={isDisconnecting}>
            <DisconnectOutlined /> Disconnect
          </DisconnectBtn>
        </ActionRow>
      </PlatformCard>
    );
  }

  return (
    <>
      <PlatformCard>
        <CardHeader>
          <ShoppingCartOutlined style={{ fontSize: 22, color: "#ed6928" }} />
          <CardTitle>eBay</CardTitle>
        </CardHeader>
        <CardDesc>
          Link your eBay account to publish products and manage marketplace sales.
        </CardDesc>
        <ConnectBtn onClick={() => setIsModalVisible(true)} disabled={isInitiating}>
          <ShoppingCartOutlined />
          Connect eBay
        </ConnectBtn>
      </PlatformCard>

      <Modal
        title={<span><ShoppingCartOutlined /> Connect to eBay</span>}
        open={isModalVisible}
        onOk={handleConnect}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={isInitiating}
        okText="Connect"
        cancelText="Cancel"
      >
        <div style={{ marginBottom: "16px" }}>
          <label style={{ display: "block", marginBottom: 8 }}>Marketplace</label>
          <Select
            value={marketplaceId}
            onChange={setMarketplaceId}
            style={{ width: "100%" }}
            options={[
              { value: "EBAY_US", label: "eBay US" },
              { value: "EBAY_GB", label: "eBay UK" },
              { value: "EBAY_DE", label: "eBay Germany" },
              { value: "EBAY_CA", label: "eBay Canada" },
            ]}
          />
          <p style={{ marginTop: 12, fontSize: 12, color: "#999" }}>
            You will be redirected to eBay to sign in and authorize this app.
          </p>
        </div>
      </Modal>
    </>
  );
};
